<template>
  <v-stepper-vertical-item
    :value="2"
    :elevation="0"
    :class="{
      'step-completed': currentStep > 2,
      'step-error': step3HasErrors,
    }"
  >
    <template #title>
      <div class="subtitle1 step-title">{{ $t("modals.allowCloudService.step2.title") }}</div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > 2" icon="$check" />
      <span v-else-if="step3HasErrors">!</span>
      <span v-else>3</span>
    </template>
    <div>
      <p
        class="body2 mb-6"
        v-html="
          isOtherProvider
            ? $t('modals.allowCloudService.step2.descriptionOther')
            : $t('modals.allowCloudService.step2.description', { email: defaultAccountEmail })
        "
      />

      <div class="subtitle2">
        {{
          isOtherProvider
            ? $t("modals.allowCloudService.step2.newEmailTitleOther")
            : $t("modals.allowCloudService.step2.newEmailTitle")
        }}
      </div>
      <v-text-field
        data-testid="add-admin-modal-lastname"
        class="mt-4"
        variant="outlined"
        :persistent-hint="true"
        :rules="emailRules"
        :hint="$t('modals.allowCloudService.step2.newEmailHint')"
        :label="$t('modals.allowCloudService.step2.enterEmail')"
        :aria-label="$t('modals.allowCloudService.step2.enterEmail')"
        :placeholder="$t('modals.allowCloudService.step2.enterEmail')"
        :model-value="modelValue.customEmail"
        @update:modelValue="onCustomEmailChange"
      >
      </v-text-field>

      <div class="mt-5 d-flex">
        <v-btn
          color="primary"
          rounded
          :disabled="step3HasErrors"
          :loading="loading"
          @click="moveToNextStep"
        >
          {{ $t("modals.allowCloudService.step2.actionBtn") }}
        </v-btn>
        <v-btn class="ml-2" variant="text" color="primary" @click="goBack">
          {{ $t("general.back") }}
        </v-btn>
      </div>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import {
  SatConfigurationStatus,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
} from "@/constants/security-awareness.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import Patterns from "@/constants/patterns.ts";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import { useI18n } from "vue-i18n";

export default defineComponent({
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
  },
  emits: ["update:modelValue", "next", "back"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const securityAwarenessStore = useSecurityAwarenessModule();
    const { email: defaultAccountEmail } = useAccountStore();
    const loading = ref(false);

    const formValue = computed(() => props.modelValue);

    const updateLocalValue = (partial: Partial<SatConnectionConfigForm>) => {
      emit("update:modelValue", { ...props.modelValue, ...partial });
    };

    const isOtherProvider = computed(() => {
      return formValue.value.selectedService === "other";
    });

    const emailRules = [
      (email: string) => {
        if (isOtherProvider.value && !email) {
          return i18n.t("validations.required");
        }

        if (email) {
          if (!Patterns.EMAIL.test(email)) {
            return i18n.t("validations.email");
          }

          if (isOtherProvider.value) {
            const emailDomain = email.split("@")[1];

            if (emailDomain.toLowerCase() !== formValue.value.domain.toLowerCase()) {
              return i18n.t("validations.satCustomTestEmailDomain", {
                domain: formValue.value.domain,
              });
            }
          }
        }
        return true;
      },
    ];

    const step3HasErrors = computed(() => {
      const isEmailInvalid = emailRules[0](formValue.value.customEmail) !== true;

      return props.currentStep === 2 && isOtherProvider.value && isEmailInvalid;
    });

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[formValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const moveToNextStep = async () => {
      loading.value = true;
      await securityAwarenessStore.updateSatConfig(
        {
          status: SatConfigurationStatus.EMAIL_SENT,
          testEmailAddresses: formValue.value.customEmail
            ? [formValue.value.customEmail]
            : [defaultAccountEmail],
        },
        satConfigurationProvider.value
      );
      await securityAwarenessStore.sendTestEmails(satConfigurationProvider.value);
      loading.value = false;
      emit("next");
    };

    const onCustomEmailChange = (value: string) => {
      updateLocalValue({ customEmail: value });
    };

    const goBack = () => {
      emit("back");
    };

    return {
      defaultAccountEmail,
      emailRules,
      step3HasErrors,
      loading,
      isOtherProvider,
      moveToNextStep,
      onCustomEmailChange,
      goBack,
    };
  },
});
</script>
